/*
 * Hover-to-play for product gallery videos (PDP).
 *
 * Requirement: on a video media in the product gallery, preview its mp4 (muted,
 * looping) over the poster. Playback starts automatically when
 * hidden_video_play_icon is enabled; otherwise hover starts it and mouse-out
 * stops it, restoring the static poster.
 *
 * How it works: for each `.product-media-container.media-type-video` inside the
 * main <media-gallery>, we reuse the mp4 <source> already present in Shopify's
 * deferred-media <template> to build a muted/looping/inline <video> overlay.
 * With hidden_video_play_icon enabled, the overlay is built and played once it
 * is initialized (including touch devices), and pointer events do not stop it.
 * Otherwise mouseenter -> play (overlay fades in over the poster); mouseleave ->
 * pause + rewind + fade out (poster shows). That hover mode only runs on
 * hover-capable pointers; touch devices keep default tap-to-play untouched.
 *
 * Pure vanilla JS, no dependencies. Included from main-product.liquid. Styling
 * (.pg-hover-video / .pg-hover-playing) lives in section-main-product.css.
 */
(function () {
  'use strict';

  var hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');
  var script = document.currentScript || document.querySelector('script[data-hidden-video-play-icon]');
  var hiddenVideoPlayIcon = script ? script.getAttribute('data-hidden-video-play-icon') === 'true' : false;

  function getSources(container) {
    var tpl = container.querySelector('deferred-media template');
    var video = tpl && tpl.content ? tpl.content.querySelector('video') : null;
    if (!video) return [];
    return Array.prototype.map
      .call(video.querySelectorAll('source'), function (s) {
        return { src: s.getAttribute('src'), type: s.getAttribute('type') || 'video/mp4' };
      })
      .filter(function (s) { return s.src; });
  }

  function setup(container, autoplay) {
    if (container.__pgHoverInit) return;
    var sources = getSources(container);
    if (!sources.length) return; // no playable mp4 — leave default behaviour
    container.__pgHoverInit = true;

    var video = null;
    var hovering = false;
    var autoplayMode = autoplay === true;
    var start = function () {
      if (!video) build();
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
      if (video.readyState >= 3) reveal();
    };

    function reveal() {
      // Only show the overlay once a frame is actually rendering and playback is
      // still wanted — avoids a black flash before the first frame is available.
      if (hovering || autoplayMode) container.classList.add('pg-hover-playing');
    }

    function build() {
      video = document.createElement('video');
      video.className = 'pg-hover-video';
      // Match the poster's fit so the overlay lines up exactly.
      video.style.objectFit = container.classList.contains('media-fit-cover') ? 'cover' : 'contain';
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('preload', 'none');
      video.setAttribute('tabindex', '-1');
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('playing', reveal);
      sources.forEach(function (s) {
        var el = document.createElement('source');
        el.src = s.src;
        el.type = s.type;
        video.appendChild(el);
      });
      container.appendChild(video);
    }

    function enter() {
      if (autoplayMode || !hoverMQ.matches) return;
      hovering = true;
      start();
      // Already buffered (re-hover): show immediately; otherwise wait for 'playing'.
    }

    function leave() {
      if (autoplayMode) return;
      hovering = false;
      container.classList.remove('pg-hover-playing');
      if (video) {
        try { video.pause(); video.currentTime = 0; } catch (e) {}
      }
    }

    if (autoplayMode) start();

    container.addEventListener('mouseenter', enter);
    container.addEventListener('mouseleave', leave);
  }

  function init(autoplay) {
    var autoplayMode = autoplay === true || hiddenVideoPlayIcon;
    if (!autoplayMode && !hoverMQ.matches) return; // touch / no-hover: keep default tap-to-play
    var galleries = document.querySelectorAll('media-gallery');
    Array.prototype.forEach.call(galleries, function (g) {
      var vids = g.querySelectorAll('.product-media-container.media-type-video');
      Array.prototype.forEach.call(vids, function (container) {
        setup(container, autoplayMode);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Gallery can be injected late (variant change / theme editor); re-scan.
  window.addEventListener('load', init);
})();
